import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Aviso de "aguarde" para um bloco que ainda não tem nada para mostrar — uma tabela sem
 * linhas, uma seção inteira. Antes dele, uma tabela carregando pela primeira vez era só o
 * cabeçalho sobre um espaço em branco, e não dava para distinguir "vindo" de "vazio".
 *
 * Se a espera passar de alguns segundos, uma segunda linha avisa que ainda está buscando,
 * para quem está olhando não achar que a tela travou.
 */

const ESPERA_LONGA_MS = 6000;

type CarregandoConteudoProps = {
  mensagem?: string;
  /** Texto que aparece depois de `ESPERA_LONGA_MS` sem resposta. */
  mensagemDeEsperaLonga?: string;
  className?: string;
};

export function CarregandoConteudo({
  mensagem = "Carregando dados…",
  mensagemDeEsperaLonga = "Ainda buscando. Períodos longos podem levar mais tempo.",
  className,
}: CarregandoConteudoProps) {
  const [esperaLonga, setEsperaLonga] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setEsperaLonga(true), ESPERA_LONGA_MS);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-10 text-center text-muted-foreground",
        className,
      )}
    >
      <Loader2
        aria-hidden="true"
        className="h-6 w-6 animate-spin text-primary"
      />
      <span className="text-sm font-medium">{mensagem}</span>
      {esperaLonga && <span className="text-xs">{mensagemDeEsperaLonga}</span>}
    </div>
  );
}

/**
 * Selo "Atualizando…" sobre um bloco que **já tem** conteúdo e está buscando outro (troca de
 * filtro, de página, de ordenação). Fica no alto, e não no meio, porque numa tabela longa o
 * meio pode estar fora da tela. O pai precisa ser `relative`.
 */
export function AtualizandoConteudo({
  mensagem = "Atualizando dados…",
}: {
  mensagem?: string;
}) {
  return (
    <div className="absolute inset-0 z-10 flex items-start justify-center bg-background/50 pt-10 backdrop-blur-[1px]">
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm text-muted-foreground shadow-sm"
      >
        <Loader2
          aria-hidden="true"
          className="h-4 w-4 animate-spin text-primary"
        />
        {mensagem}
      </div>
    </div>
  );
}
